/**
 * Whether a licence may be used at all (#582): these two, and only these.
 *
 *  - `active`    the normal state: its users can sign in and work;
 *  - `inactive`  deactivated from the Danger zone. Its users are refused at
 *                sign-in and their sessions are revoked, but the licence keeps
 *                everything it owns (users, lists and credits are untouched)
 *                so an administrator can set it back to `active` at any time.
 *
 * Before #582 the column was a free string that nothing but the database default
 * ever wrote, and the rule was "anything that is not `active` reads as inactive".
 * That rule still holds for reading older rows; `LICENCE_STATUSES` is what the
 * API now accepts and returns.
 *
 * Deactivating is deliberately NOT a deletion and NOT a credit operation: no row
 * is removed, no credit is consumed or refunded.
 */

export const LICENCE_STATUSES = ['active', 'inactive'] as const;

export type LicenceStatus = (typeof LICENCE_STATUSES)[number];

const LICENCE_STATUS_SET: ReadonlySet<string> = new Set(LICENCE_STATUSES);

/** True when `value` is one of `LICENCE_STATUSES`. */
export function isLicenceStatus(value: unknown): value is LicenceStatus {
    return typeof value === 'string' && LICENCE_STATUS_SET.has(value);
}

/**
 * True when the licence may be used. Deliberately "is it exactly `active`" rather
 * than "is it `inactive`": a row written before #582 could carry anything, and an
 * unrecognised status must read as closed, never as open.
 */
export function isLicenceActive(status: string): boolean {
    return status === 'active';
}
